import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Button,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import AccountingSettingService from "../../../services/AccountingServices/AccountingSettingService";
import LedgersService from "../../../services/AccountingServices/LedgerService";
import TableSearch from "../../../Components/Common/TableSearch";

const DefaultLedgerSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [selectedLedgerId, setSelectedLedgerId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [settingsData, ledgersResponse] = await Promise.all([
        AccountingSettingService.getDefaultLedgerSettings(),
        LedgersService.getList({ pageSize: 1000 }), 
      ]);
      
      setSettings(settingsData || []);
      
      const ledgerList = Array.isArray(ledgersResponse) ? ledgersResponse : ledgersResponse?.items || [];
      const formattedLedgers = ledgerList.map((l) => ({
        value: l.id,
        label: `${l.name} (${l.code})`,
      }));
      setLedgers(formattedLedgers);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load settings data");
    } finally {
      setLoading(false);
    }
  };

  const toggleEditModal = (setting = null) => {
    if (setting) {
      setSelectedSetting(setting);
      setSelectedLedgerId(setting.ledgerId);
    } else {
      setSelectedSetting(null);
      setSelectedLedgerId(null);
    }
    setIsEditModalOpen(!isEditModalOpen);
  };

  const handleUpdate = async () => {
    if (!selectedSetting || !selectedLedgerId) return;

    try {
      setUpdating(true);
      await AccountingSettingService.updateDefaultLedgerSetting(
        selectedSetting.key,
        selectedLedgerId
      );
      toast.success(`Successfully updated ${selectedSetting.key} mapping`);
      fetchInitialData();
      toggleEditModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update mapping");
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "System Key",
      selector: (row) => row.key,
      sortable: true,
      cell: (row) => (
        <span className="fs-12 px-2 py-1">
          {row.key}
        </span>
      ),
    },
    {
      name: "Mapped Ledger",
      selector: (row) => row.ledgerName,
      sortable: true,
      cell: (row) => (
        <div className="fw-semibold text-primary">
          {row.ledgerName || <span className="text-muted fw-normal fst-italic">Not Assigned</span>}
        </div>
      ),
    },
    {
      name: "Ledger Code",
      selector: (row) => row.ledgerCode,
      sortable: true,
      cell: (row) => <span className="text-muted">{row.ledgerCode || "-"}</span>,
    },
  ];

  const stats = [
    {
      title: "Total Mappings",
      value: settings.length,
      color: "#4A90E2",
      icon: "ri-links-line",
    },
    {
      title: "Assigned",
      value: settings.filter(s => s.ledgerId).length,
      color: "#27AE60",
      icon: "ri-checkbox-circle-line",
    },
    {
      title: "Unassigned",
      value: settings.filter(s => !s.ledgerId).length,
      color: "#E74C3C",
      icon: "ri-error-warning-line",
    },
  ];

  const filteredSettings = useMemo(() => {
    if (!searchTerm) return settings;
    const lowerSearch = searchTerm.toLowerCase();
    return settings.filter(
      (s) =>
        s.key?.toLowerCase().includes(lowerSearch) ||
        s.ledgerName?.toLowerCase().includes(lowerSearch) ||
        s.ledgerCode?.toLowerCase().includes(lowerSearch)
    );
  }, [settings, searchTerm]);

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Default Ledger Settings"
        pageTitle="Accounting Setup"
        mobileCardTitle={null}
        data={filteredSettings}
        columns={columns}
        stats={stats}
        onEdit={toggleEditModal}
        loading={loading}
        searchKeys={["key", "ledgerName", "ledgerCode"]}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        showSearch={false}
        emptyStateMessage="No ledger mappings found"
        customHeaderContent={
          <Row className="mb-3 align-items-center">
            <Col lg={3}>
              <h5 className="mb-1 fw-bold text-dark">Mapping List</h5>
              <p className="text-muted small mb-0">System default ledger mappings</p>
            </Col>
            <Col md={9}>
              <div className="d-flex justify-content-end">
                <div style={{ flex: '1 1 auto', minWidth: '150px', maxWidth: '300px' }}>
                  <TableSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search mappings..."
                  />
                </div>
              </div>
            </Col>
          </Row>
        }
      />

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} toggle={() => toggleEditModal()} centered>
        <ModalHeader toggle={() => toggleEditModal()} className="bg-light">
          Edit Mapping: <span className="text-primary">{selectedSetting?.key}</span>
        </ModalHeader>
        <ModalBody>
          <FormGroup className="mb-0">
            <Label className="fw-bold">Select Ledger</Label>
            <Select
              options={ledgers}
              value={ledgers.find((l) => l.value === selectedLedgerId)}
              onChange={(opt) => setSelectedLedgerId(opt.value)}
              placeholder="Select a ledger account..."
              classNamePrefix="react-select"
              isSearchable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <small className="text-muted d-block mt-2">
              This account will be used as the default for all <strong>{selectedSetting?.key}</strong> transactions.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-0">
          <Button color="light" onClick={() => toggleEditModal()}>Cancel</Button>
          <Button 
            color="primary" 
            onClick={handleUpdate} 
            disabled={updating || !selectedLedgerId}
          >
            {updating ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DefaultLedgerSettings;
